import { coerceCartEventKey, type EventRouteKey } from './event-route-key.util';

/** Slug from API (`slug` / `event_slug` / `url_slug`). */
export function getEventSlug(record: Record<string, unknown> | null | undefined): string {
  if (!record) return '';
  const raw = record['slug'] ?? record['event_slug'] ?? record['url_slug'];
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  return '';
}

/** URL-safe slug from event title. */
export function slugifyEventTitle(value: string): string {
  return (
    value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'event'
  );
}

/** API slug, else title slug, else lowercased event id (never empty — avoids redirect loops). */
export function resolveCanonicalEventSlug(
  record: Record<string, unknown> | null | undefined,
  eventId?: EventRouteKey
): string {
  const fromApi = getEventSlug(record);
  if (fromApi) return fromApi;
  const title =
    record?.['title'] ?? record?.['name'] ?? record?.['event_name'] ?? record?.['event_title'];
  if (typeof title === 'string' && title.trim()) {
    return slugifyEventTitle(title);
  }
  if (eventId != null) {
    const id = String(eventId).trim();
    if (id) return id.toLowerCase();
  }
  return 'event';
}

export type EventNavInput =
  | EventRouteKey
  | { id?: EventRouteKey; slug?: string; [key: string]: unknown, event_status?: string } // eslint-disable-line @typescript-eslint/naming-convention;

/** Resolve id + slug from an event record or bare id. */
export function resolveEventNav(
  input: EventNavInput | null | undefined
): { eventId: EventRouteKey; slug: string } | null {
  if (input == null) return null;
  if (typeof input === 'object') {
    const eventId = coerceCartEventKey(input.id);
    if (eventId == null) return null;
    const slug =
      (typeof input.slug === 'string' && input.slug.trim()) ||
      getEventSlug(input as Record<string, unknown>);
    return { eventId, slug };
  }
  const eventId = coerceCartEventKey(input);
  if (eventId == null) return null;
  return { eventId, slug: '' };
}

/** Router commands — always `/event/:slug/:id/...` (canonical). */
export function buildEventRoute(
  eventId: EventRouteKey,
  slug?: string | null,
  ...suffix: string[]
): (string | number)[] {
  const id = String(eventId);
  const s = (slug ?? '').trim() || resolveCanonicalEventSlug(null, eventId);
  return suffix.length ? ['/event', s, id, ...suffix] : ['/event', s, id];
}

export function buildEventRouteFromInput(
  input: EventNavInput | null | undefined,
  ...suffix: string[]
): (string | number)[] | null {
  const nav = resolveEventNav(input);
  if (!nav) return null;
  const slug =
    nav.slug ||
    (typeof input === 'object' && input
      ? resolveCanonicalEventSlug(input as Record<string, unknown>, nav.eventId)
      : resolveCanonicalEventSlug(null, nav.eventId));
  return buildEventRoute(nav.eventId, slug, ...suffix);
}
